import { ChatMessage } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";

import {
  fetchTaskDetail,
  retryTaskWriteback,
  sendTaskReply,
  updateTaskStatus,
} from "../../../api/tasks/tasksApi";
import {
  TaskDetailResponse,
  TaskWritebackResponse,
  TaskWritebackStatus,
  ThreadMessage,
} from "../../../api/tasks/taskTypes";
import { OrganizationType } from "../../../enums/organizationTypes";
import {
  formatCalendarDate,
  formatMessageTimestamp,
  formatServiceDate,
} from "../../../utils/dateFormatter";
import RecipientChips from "./recipientChips";
import RecipientPicker from "./recipientPicker";
import TaskDocumentsTab from "./taskDocumentsTab";

interface TaskDetailModalProps {
  taskRowId: string | null;

  onClose: (statusChange?: { taskRowId: string; taskStatus: string }) => void;
}

const STATUS_PILL: Record<
  string,
  { textColor: string; backgroundColor: string }
> = {
  complete: { textColor: "#005D49", backgroundColor: "#E6F3F0" },
  completed: { textColor: "#005D49", backgroundColor: "#E6F3F0" },
  inprogress: { textColor: "#C24400", backgroundColor: "#FFF3E0" },
  "in progress": { textColor: "#C24400", backgroundColor: "#FFF3E0" },
  open: { textColor: "#CC0300", backgroundColor: "#FFE8E8" },
};

const OUTCOME_PILL: Record<string, { color: string; bg: string }> = {
  pending: { color: "#665D00", bg: "#FFFCD6" },
  approved: { color: "#005D49", bg: "#E6F3F0" },
  denied: { color: "#CC0300", bg: "#FFD6D6" },
  "not started": { color: "#FFFFFF", bg: "#404040" },
};

const NEUTRAL_PILL = { textColor: "#2E2E2E", backgroundColor: "#F5F5F5" };

const STATUS_OPTIONS: { value: TaskWritebackStatus; label: string }[] = [
  { value: "Open", label: "Open" },
  { value: "InProgress", label: "In Progress" },
  { value: "Complete", label: "Complete" },
];

type DetailTab = "task" | "documents";

const Field = ({ label, value }: { label: string; value: string | null }) => (
  <div className="flex flex-col gap-1">
    <span className="text-tiny text-primaryGray-7">{label}</span>
    <span className="text-small font-semiBold text-primaryGray-2">
      {value && value.trim() !== "" ? value : "--"}
    </span>
  </div>
);

const Pill = ({
  label,
  color,
  bg,
}: {
  label: string;
  color: string;
  bg: string;
}) => (
  <span
    className="w-fit rounded px-2 py-0.5 text-tiny font-semiBold"
    style={{ color, backgroundColor: bg }}
  >
    {label}
  </span>
);

const TaskDetailModal = ({ taskRowId, onClose }: TaskDetailModalProps) => {
  const [detail, setDetail] = useState<TaskDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("task");

  const [extraMessages, setExtraMessages] = useState<ThreadMessage[]>([]);
  const [replyText, setReplyText] = useState("");
  const [replyState, setReplyState] = useState<"idle" | "sending" | "failed">(
    "idle",
  );
  const [replyKey, setReplyKey] = useState<string | null>(null);
  const [pendingReplyText, setPendingReplyText] = useState("");

  const [statusValue, setStatusValue] = useState<string>("");
  const [statusState, setStatusState] = useState<"idle" | "saving" | "failed">(
    "idle",
  );
  const [statusKey, setStatusKey] = useState<string | null>(null);
  const [statusAttempt, setStatusAttempt] =
    useState<TaskWritebackStatus | null>(null);
  const [initialStatus, setInitialStatus] = useState<string>("");
  const [committedStatus, setCommittedStatus] = useState<string>("");

  useEffect(() => {
    if (!taskRowId) return;
    setActiveTab("task");
    setDetail(null);
    setError(null);
    setLoading(true);
    setExtraMessages([]);
    setReplyText("");
    setReplyState("idle");
    setReplyKey(null);
    setPendingReplyText("");
    setStatusState("idle");
    setStatusKey(null);
    setStatusAttempt(null);
    setStatusValue("");
    setInitialStatus("");
    setCommittedStatus("");
    const controller = new AbortController();
    fetchTaskDetail(
      taskRowId,
      OrganizationType.ASTERA_PRIMARY_CARE,
      controller.signal,
    )
      .then((data) => {
        setDetail(data);
        const loadedStatus = data.task?.task_status ?? "";
        setStatusValue(loadedStatus);
        setInitialStatus(loadedStatus);
        setCommittedStatus(loadedStatus);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError("Failed to load task details.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [taskRowId]);

  if (taskRowId === null) return null;

  const task = detail?.task;
  const outcome = OUTCOME_PILL[(task?.outcome_status ?? "").toLowerCase()];

  const runReply = async (
    optimisticText: string,
    send: () => Promise<TaskWritebackResponse>,
  ) => {
    setReplyState("sending");
    const optimistic: ThreadMessage = {
      sender: "RISA",
      text: optimisticText,
      sent_at: new Date().toISOString(),
    };
    setExtraMessages((prev) => [...prev, optimistic]);
    try {
      const result = await send();
      if (result.status === "sent") {
        setReplyState("idle");
        setReplyText("");
        setReplyKey(null);
        setPendingReplyText("");
      } else {
        setReplyState("failed");
        setExtraMessages((prev) => prev.filter((m) => m !== optimistic));
      }
    } catch {
      setReplyState("failed");
      setExtraMessages((prev) => prev.filter((m) => m !== optimistic));
    }
  };

  const handleSendReply = () => {
    if (!taskRowId || replyState === "sending") return;
    const message = replyText.trim();
    if (!message) return;
    const key = crypto.randomUUID();
    setReplyKey(key);
    setPendingReplyText(message);
    void runReply(message, () => sendTaskReply(taskRowId, message, key));
  };

  const handleRetryReply = () => {
    if (!taskRowId || !replyKey) return;
    void runReply(pendingReplyText, () => retryTaskWriteback(replyKey));
  };

  const runStatus = async (
    target: TaskWritebackStatus,
    send: () => Promise<TaskWritebackResponse>,
  ) => {
    const previous = committedStatus;
    setStatusValue(target);
    setStatusState("saving");
    try {
      const result = await send();
      if (result.status === "sent") {
        setStatusState("idle");
        setCommittedStatus(target);
        setStatusAttempt(null);
      } else {
        setStatusValue(previous);
        setStatusState("failed");
      }
    } catch {
      setStatusValue(previous);
      setStatusState("failed");
    }
  };

  const handleStatusChange = (next: TaskWritebackStatus) => {
    if (!taskRowId || statusState === "saving") return;
    const key = crypto.randomUUID();
    setStatusKey(key);
    setStatusAttempt(next);
    void runStatus(next, () => updateTaskStatus(taskRowId, next, key));
  };

  const handleRetryStatus = () => {
    if (!taskRowId || !statusKey || !statusAttempt) return;
    void runStatus(statusAttempt, () => retryTaskWriteback(statusKey));
  };

  const handleClose = () => {
    if (taskRowId && committedStatus && committedStatus !== initialStatus) {
      onClose({ taskRowId, taskStatus: committedStatus });
    } else {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60"
      onClick={handleClose}
    >
      <div
        className="flex h-[82vh] w-[72%] max-w-[1100px] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between border-b border-primaryGray-14 px-5 py-3">
          <h2 className="text-h11 font-bold text-primaryGray-1">Tasks</h2>
          <button
            type="button"
            className="text-primaryGray-7 hover:text-primaryGray-2"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        {/* Tabs */}
        <div className="flex shrink-0 gap-5 border-b border-primaryGray-14 px-5">
          {(["task", "documents"] as DetailTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`py-2 text-small ${
                activeTab === tab
                  ? "border-b-2 border-tertiaryBlue-5 font-bold text-tertiaryBlue-5"
                  : "text-primaryGray-7"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "task" ? "Task" : "Documents"}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden p-5">
          {loading ? (
            <div className="text-small text-primaryGray-7">Loading…</div>
          ) : error ? (
            <div className="text-small text-tertiaryRed-3">{error}</div>
          ) : !detail || !task ? (
            <div className="text-small text-primaryGray-7">
              No details found.
            </div>
          ) : activeTab === "task" ? (
            <div className="flex h-full flex-col gap-4">
              {/* Task Name + status control */}
              <div className="flex shrink-0 items-center justify-between rounded border border-primaryGray-14 px-4 py-3">
                <span className="text-small font-bold text-primaryGray-1">
                  {task.title ?? "Task"}
                </span>
                <div className="flex items-center gap-2">
                  {statusState === "saving" && (
                    <span className="text-tiny text-primaryGray-7">
                      Saving…
                    </span>
                  )}
                  {statusState === "failed" && (
                    <button
                      type="button"
                      className="text-tiny font-semiBold text-tertiaryRed-3 hover:underline"
                      onClick={handleRetryStatus}
                    >
                      Failed — Retry
                    </button>
                  )}
                  <select
                    value={statusValue}
                    disabled={statusState === "saving"}
                    onChange={(e) =>
                      handleStatusChange(e.target.value as TaskWritebackStatus)
                    }
                    className="rounded border border-primaryGray-14 px-2 py-1 text-tiny font-semiBold text-primaryGray-2 focus:border-tertiaryBlue-5 focus:outline-none"
                    style={(() => {
                      const c =
                        STATUS_PILL[statusValue.toLowerCase()] ?? NEUTRAL_PILL;
                      return {
                        color: c.textColor,
                        backgroundColor: c.backgroundColor,
                      };
                    })()}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Two panes */}
              <div className="flex min-h-0 flex-1 gap-4">
                {/* Left: metadata grid */}
                <div className="w-[34%] shrink-0 overflow-auto border-r border-primaryGray-15 pr-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <Field
                      label="Task ID"
                      value={task.task_id.replace(/^-/, "")}
                    />
                    <Field
                      label="Date of Service"
                      value={formatServiceDate(task.created_at)}
                    />
                    <Field label="Patient Details" value={task.patient_name} />
                    <Field
                      label="Date of Birth"
                      value={formatCalendarDate(task.patient_dob)}
                    />
                    <Field
                      label="Provider Details"
                      value={task.provider_name}
                    />
                    <Field label="Drug" value={task.drug} />
                    <Field label="Key" value={task.cmm_key} />
                    <Field label="Inbox Name" value={task.inbox_name} />
                    <div className="flex flex-col gap-1">
                      <span className="text-tiny text-primaryGray-7">
                        Task Category
                      </span>
                      <Pill
                        label={task.bucket ?? "--"}
                        color={NEUTRAL_PILL.textColor}
                        bg={NEUTRAL_PILL.backgroundColor}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-tiny text-primaryGray-7">
                        Outcome
                      </span>
                      <Pill
                        label={task.outcome_status ?? "--"}
                        color={outcome?.color ?? NEUTRAL_PILL.textColor}
                        bg={outcome?.bg ?? NEUTRAL_PILL.backgroundColor}
                      />
                    </div>
                  </div>

                  {/* Current recipients as chips, each with a remove (✕) control */}
                  <RecipientChips
                    key={`chips-${taskRowId}`}
                    taskRowId={taskRowId}
                    orgId={OrganizationType.ASTERA_PRIMARY_CARE}
                    recipients={detail.recipients}
                    inboxRecipientUserId={detail.task.inbox_recipient_user_id}
                  />

                  {/* Add recipient (name typeahead over the OncoEMR directory) */}
                  <RecipientPicker
                    key={taskRowId}
                    taskRowId={taskRowId}
                    orgId={OrganizationType.ASTERA_PRIMARY_CARE}
                  />
                </div>

                {/* Right: conversation thread */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="flex-1 overflow-auto pr-1">
                    {detail.thread.length === 0 &&
                    extraMessages.length === 0 ? (
                      <p className="text-small text-primaryGray-7">
                        No messages in this thread yet.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {[...detail.thread, ...extraMessages].map(
                          (message, index) => (
                            <ChatMessage
                              key={index}
                              senderName={
                                message.sender === "RISA"
                                  ? "RISA"
                                  : (message.sender_name ?? "Other")
                              }
                              timestamp={formatMessageTimestamp(
                                message.sent_at,
                              )}
                              message={message.text ?? ""}
                              side={
                                message.sender === "RISA" ? "right" : "left"
                              }
                              avatarLabel={
                                message.sender === "RISA" ? "RI" : "OT"
                              }
                              maxWidth="80%"
                            />
                          ),
                        )}
                      </div>
                    )}
                  </div>

                  {/* Reply composer */}
                  <div className="mt-3 flex shrink-0 flex-col gap-1 border-t border-primaryGray-15 pt-3">
                    {replyState === "failed" && (
                      <div className="flex items-center gap-2 text-tiny text-tertiaryRed-3">
                        <span>Reply failed to send.</span>
                        <button
                          type="button"
                          className="font-semiBold hover:underline"
                          onClick={handleRetryReply}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={replyText}
                        disabled={replyState === "sending"}
                        placeholder="Type a reply…"
                        className="flex-1 rounded border border-primaryGray-14 px-3 py-2 text-small text-primaryGray-2 focus:border-tertiaryBlue-5 focus:outline-none disabled:bg-primaryGray-16"
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                        }}
                      />
                      <button
                        type="button"
                        disabled={
                          replyState === "sending" || replyText.trim() === ""
                        }
                        className="rounded bg-tertiaryBlue-5 px-4 py-2 text-small font-semiBold text-white disabled:cursor-not-allowed disabled:bg-primaryGray-14 disabled:text-primaryGray-9"
                        onClick={handleSendReply}
                      >
                        {replyState === "sending" ? "Sending…" : "Send"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <TaskDocumentsTab
              taskId={task.task_id}
              documentIds={detail.document_ids}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
